import { ReactElement, useRef, useState } from 'react';
import { Typography } from 'antd';
import { ContainerOutlined, DeleteOutlined, InboxOutlined } from '@ant-design/icons';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import { StyledIconWrapper } from 'components/new-ui/base/wrappers/styles';
import { archiveIcs2EnsGroup, deleteIcs2EnsGroup, unarchiveIcs2EnsGroup } from 'store/declarations/client';
import { runWithNotification } from 'utils/runWithNotification';
import { GroupActionEvents } from '../DeclarationColumns';

type ConfirmAction = 'archive' | 'unarchive' | 'delete';

type ModalState = { type: 'none' } | { type: ConfirmAction; id: string } | { type: 'duplicate'; id: string };

type Confirmation = {
    title: string;
    confirmButtonText: string;
    icon: ReactElement;
    message: string;
    success: string;
    failure: string;
    request: (sourceManifestId: string) => Promise<unknown>;
};

const confirmations: Record<ConfirmAction, Confirmation> = {
    archive: {
        title: 'Archive Group',
        confirmButtonText: 'Archive',
        icon: <ContainerOutlined />,
        message: 'Every ENS declaration created from this manifest will be archived.',
        success: 'Group archived successfully!',
        failure: 'Could not archive the group!',
        request: archiveIcs2EnsGroup,
    },
    unarchive: {
        title: 'Unarchive Group',
        confirmButtonText: 'Unarchive',
        icon: <InboxOutlined />,
        message: 'Every ENS declaration created from this manifest will be unarchived.',
        success: 'Group unarchived successfully!',
        failure: 'Could not unarchive the group!',
        request: unarchiveIcs2EnsGroup,
    },
    delete: {
        title: 'Delete Group',
        confirmButtonText: 'Delete',
        icon: <DeleteOutlined />,
        message:
            'Every ENS declaration created from this manifest will be deleted. This can only be done while they are all still drafts, and cannot be undone.',
        success: 'Group deleted successfully!',
        // The service refuses the whole group unless every declaration is a draft.
        failure: 'Only groups where every ENS is still a draft can be deleted.',
        request: deleteIcs2EnsGroup,
    },
};

const isConfirmAction = (modal: ModalState): modal is { type: ConfirmAction; id: string } =>
    modal.type in confirmations;

type Options = {
    onDone: () => void;
};

const useIcs2GroupActions = ({
    onDone,
}: Options): { groupActions: GroupActionEvents; groupActionModals: ReactElement } => {
    const [modal, setModal] = useState<ModalState>({ type: 'none' });
    const [isRunning, setIsRunning] = useState(false);

    const close = () => setModal({ type: 'none' });

    const run = async (action: () => Promise<unknown>, success: string, failure: string) => {
        setIsRunning(true);
        const succeeded = await runWithNotification(action, { success, failure });
        setIsRunning(false);

        // A failed action leaves the modal open so the user can retry or back out.
        if (!succeeded) return;
        close();
        onDone();
    };

    const groupActions: GroupActionEvents = {
        onArchiveGroup: (id) => setModal({ type: 'archive', id }),
        onUnarchiveGroup: (id) => setModal({ type: 'unarchive', id }),
        onDeleteGroup: (id) => setModal({ type: 'delete', id }),
    };

    // The three confirmations share one modal. Its contents are held past the close so the modal can
    // animate out with the text it was opened with rather than emptying first.
    const confirming = isConfirmAction(modal) ? modal : undefined;
    const shownRef = useRef<Confirmation>();
    if (confirming) shownRef.current = confirmations[confirming.type];
    const shown = shownRef.current;

    const groupActionModals = (
        <>
            {shown && (
                <ConfirmationModal
                    open={!!confirming}
                    title={shown.title}
                    confirmButtonText={shown.confirmButtonText}
                    confirmationButtonLoading={isRunning}
                    onCancel={close}
                    onClose={close}
                    onConfirm={() => {
                        if (!confirming) return;
                        const { id, type } = confirming;
                        const { request, success, failure } = confirmations[type];
                        run(() => request(id), success, failure);
                    }}
                    icon={<StyledIconWrapper>{shown.icon}</StyledIconWrapper>}
                    content={<Typography.Text>{shown.message}</Typography.Text>}
                />
            )}
        </>
    );

    return { groupActions, groupActionModals };
};

export default useIcs2GroupActions;
